import { formatISO, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'
import {
  pick as _pick,
  pickBy as _pickBy,
  result,
  set as _set,
  hasIn,
  isUndefined,
  unset,
  isNumber,
  isNull,
} from 'lodash-es'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import conf from '@/config'

const tz = conf.VITE_TIMEZONE || 'Europe/Moscow'

const numberFormatter = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})
const numberFormatterFractions = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Complex object transformer
 *
 * @param {Array} spec how to transform value by field names; if additional arg == false, all target fields should exists; e.g. [f1,f2,[f3, transformer]]
 * @param {Boolean} additional pick all fields not only from spec
 * @param {Boolean} omitEmpty skip empty (null|undefined|0) values & unset keys from result
 * @returns
 */
export function transformer(spec, additional = false, omitEmpty = false) {
  let pick = []
  const tfs = {}
  for (let sp of spec) {
    let key = sp
    let tf, tfargs
    if (Array.isArray(sp)) {
      ;[key, tf, ...tfargs] = sp
    }

    if (!tfargs) {
      tfargs = []
    }

    if (!additional) {
      pick.push(key)
    }

    if (tf instanceof Function) {
      tfs[key] = tf.bind(undefined, ...tfargs)
    } else {
      if (tf === undefined) {
        tf = 'pipe'
      }
      // string w/ tf name
      tfs[key] = v => commons[tf](v, ...tfargs)
    }
  }

  return _data => {
    let data = { ..._data }
    if (additional) {
      const allkeys = Object.keys(data)
      for (const allk of allkeys) {
        if (!hasIn(tfs, allk)) {
          tfs[allk] = commons.pipe
        }
        if (!pick.includes(allk)) {
          pick.push(allk)
        }
      }
    }
    /*
      !!!
      TODO: multiple arrays in the same path string

      ex: some.value.[].inner.some.[].val.u
      {
        some:{
          value:[
            {
              inner:{
                some:[
                  {
                    val:{
                      u: <VALUE>
                    }
                  }
                ]
              }
            }
          ]
        }
      }

    */

    const _d = _pick(data, pick)
    for (const [k, tf] of Object.entries(tfs)) {
      const ka = k.split('.')
      let ai = ka.indexOf('[]')

      if (ai !== -1) {
        const ak0 = ka.slice(0, ai).join('.')
        ai++
        const ak1 = ka.slice(ai, ka.length).join('.')
        const ar1 = result(_d, ak0)
        let a1 = ar1.map(ae => tf(result(ae, ak1)))
        _set(_d, ak0, a1)
      } else {
        let res = tf(result(_d, k))
        if (
          omitEmpty
          && ((isNumber(res) && (isNaN(res) || res == 0)) || isNull(res) || isUndefined(res))
        ) {
          unset(_d, k)
        } else {
          _set(_d, k, res)
        }
      }
    }
    return _d
  }
}

export const commons = {
  pipe: v => v,
  id: v => (v ? { id: v.id } : null),
  ids: v => (Array.isArray(v) ? v.map(({ id }) => ({ id })) : []),
  /**
   * Convert date to UTC & format ISO without ms & tz
   * @param {Date|String} v
   * @returns
   */
  dateISO: (v, fmt = "yyyy-MM-dd'T'HH:mm:ss", tz = 'UTC', locale) => {
    if (!v) {
      return null
    }
    if (!(v instanceof Date)) {
      try {
        v = parseISO(v)
      } catch (e) {
        console.error(e)
        return null
      }
    }
    if (tz === null) {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    }
    const args = [v, tz, fmt]
    if (locale) {
      args.push({ locale })
    }
    const f = formatInTimeZone(...args)
    return f
  },
  /**
   * @deprecated
   * this.dateISO should be used OR PLS use this function as shorthand
   */
  date: v =>
    v
      ? formatInTimeZone(
          v instanceof Date ? v : fromZonedTime(v, tz),
          'Europe/Moscow',
          'yyyy-MM-dd 12:00:00'
          // 'dd.MM.yyyy'
        )
      : null,
  /**
   * format given date w. given format in default TZ & ru locale
   */
  dateLocal: (dt, fmt = 'P') =>
    dt ? formatInTimeZone(fromZonedTime(dt, tz), tz, fmt, { locale: ru }) : null,
  /**
   * @deprecated
   * this.dateISO should be used OR PLS use this function as shorthand
   */
  dateTimeLocal: v => {
    return v
      ? formatInTimeZone(v instanceof Date ? v : fromZonedTime(v, tz), tz, 'dd.MM.yyyy HH:mm')
      : null
  },
  /**
   * human-readable round-off decimal
   */
  deci: decimalTransformer,
  /**
   * this.deci as String
   * @param {any} v numeric
   * @returns string
   */
  decistring: v => decimalTransformer(v).toFixed(2),
  datestring: v =>
    v
      ? formatInTimeZone(
          v instanceof Date ? v : fromZonedTime(v, tz),
          'Europe/Moscow',
          'dd.MM.yyyy'
        )
      : null,
  numberFormat: num => {
    return num % 1 == 0 ? numberFormatter.format(num) : numberFormatterFractions.format(num)
  },
  /**
   * join array into string
   * @param {Array} v
   * @returns {string|null}
   */
  concat: v => (Array.isArray(v) ? v.join(',') : null),
  arr: (v, fn, protofn = 'map') => (Array.isArray(v) ? v[protofn](fn) : v),
  medicineLabel(med) {
    if (!med) return 'Не указан'
    const l = []
    for (const k of ['name', 'dosage', 'medicine_trademark.medicinal_form.value']) {
      const v = result(med, k)
      if (!v) {
        continue
      }
      l.push(v)
    }
    if (!l.length) {
      return 'Не указан'
    }
    return l.join(', ')
  },
}

function decimalTransformer(v) {
  const n = Number(v)
  if (isNaN(n)) {
    return 0
  }
  return Math.round((n + Number.EPSILON) * 100) / 100
}

// returns uploads
// !important: value of date-formatted should be instance of Date or ISO-date formatted string
export function transformUploads(uploads, dateKeys = []) {
  return (uploads || []).map(f => {
    if (!f.metadata) {
      return f
    }
    for (let i of dateKeys) {
      if (f.metadata[i]) {
        f.metadata[i] = formatISO(f.metadata[i], { representation: 'date' })
      }
    }
    return { secure_filename: f.secure_filename, metadata: f.metadata }
  })
}

// returns uloads
export function pickUploads(uploads = [], dateKeys = []) {
  return (uploads || []).map(u => {
    if (!u.metadata) {
      return u
    }
    for (let i of dateKeys) {
      // wtf??? почему-то стала прилетать Data вместо строки
      if (u.metadata[i] && !(u.metadata[i] instanceof Date)) {
        u.metadata[i] = parseISO(u.metadata[i])
      }
    }
    return u
  })
}

export const listPickMapper = (prop, prefix) => item => {
  let val = listPick(item, prop, prefix)
  return { ...item, [prop]: val }
}

export function listPick(item, prop, prefix) {
  let val = result(item, prop)
  if (!val) {
    const pfx = Array.isArray(prefix) ? prefix : String(prefix).split('.')
    val = result(item, [...pfx, prop])
  }
  return val
}
